import React from 'react';
import { common } from '@kurtosys/ksys-app-template';
import { action, computed, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';

import { ComponentBase, InjectedStyledComponent, KEYS } from '../../../shared/index.js';
import Input from '../Input/Input.js';
import Label from '../Label/Label.js';
import { ITranslationContextValue } from '../Translate/models/index.js';
import Translate, { TranslationContext } from '../Translate/Translate.js';
import VisuallyHidden from '../VisuallyHidden/VisuallyHidden.js';

import { ISearchConfiguration, ISearchProps, ISearchResult, ISearchState, TSearchAction } from './models/index.js';
import ResultBackground from './styledComponents/ResultBackground.js';
import ResultGroup from './styledComponents/ResultGroup.js';
import ResultGroupWrapper from './styledComponents/ResultGroupWrapper.js';
import SearchBar from './styledComponents/SearchBar.js';
import SearchClear from './styledComponents/SearchClear.js';
import SearchIcon from './styledComponents/SearchIcon.js';
import SearchPill from './styledComponents/SearchPill.js';
import SelectedResultClear from './styledComponents/SelectedResultClear.js';
import SelectedResultGroup from './styledComponents/SelectedResultGroup.js';
import SelectedResultPill from './styledComponents/SelectedResultPill.js';
import SelectedResultsWrapper from './styledComponents/SelectedResultsWrapper.js';
import SelectedResultValue from './styledComponents/SelectedResultValue.js';
import Wrapper from './styledComponents/Wrapper.js';
import { SEARCH_CATEGORY_KEY, SEARCH_COMPONENT_KEY } from './Search.meta.js';
import ResultElement from './SearchResultElement.js';
import { SearchResultElements } from './SearchResultElements.js';
import ResultGroupElement from './SearchResultGroupElement.js';
import SearchStore from './SearchStore.js';

const sortByFunction = common.commonUtils.sortBy;

const SEARCH_ACTIONS: TSearchAction[] = [
	'UPDATED_TERM',
	'UPDATED_RESULTS',
	'UPDATED_RESULT_DISPLAY',
	'UPDATED_SEARCH_STATUS',
	'UPDATED_PILL_PROPS',
];

const NAVIGATION_KEYS = [
	KEYS.ESCAPE,
	KEYS.UP_ARROW,
	KEYS.DOWN_ARROW,
	KEYS.PAGE_UP,
	KEYS.PAGE_DOWN,
	KEYS.HOME,
	KEYS.END,
];

const ACCEPT_KEYS = [KEYS.ENTER, KEYS.SPACE, KEYS.SPACEBAR];

export class Search extends ComponentBase<ISearchProps, ISearchState, ISearchConfiguration> {
	categoryKey = SEARCH_CATEGORY_KEY;
	componentKey = SEARCH_COMPONENT_KEY;

	// Component properties
	translationContext: ITranslationContextValue | undefined;
	timeout: NodeJS.Timeout | undefined;
	// eslint-disable-next-line @typescript-eslint/naming-convention
	SearchStore: SearchStore;
	lastAction: TSearchAction | undefined;

	searchBarId: string;
	searchLabelId: string;
	searchInputId: string;
	searchResultsId: string;

	focusedIndex = -1;
	focusSearchInput: boolean | null = null;
	resultCount = 0;
	constructor(props: ISearchProps) {
		super(props);

		makeObservable(this, {
			focusedIndex: observable.ref,
			focusSearchInput: observable.ref,
			resultCount: observable.ref,
			term: computed,
			isSearching: computed,
			pillProps: computed,
			selectedResults: computed,
			removeSelectedResult: action,
			results: computed,
			sortedResults: computed,
			groupedResults: computed,
			resultGroupedElements: computed,
			resultElements: computed,
			searchLabel: computed,
			searchResults: computed,
			showSelectedResults: computed,
			onNavigation: action,
			handleKeyDown: action,
			setFocusedItem: action,
		});

		const guid = common.commonUtils.generateGuid();
		this.searchBarId = `searchBar_${guid}`;
		this.searchInputId = `searchInput_${guid}`;
		this.searchLabelId = `searchLabel_${guid}`;
		this.searchResultsId = `searchResults_${guid}`;
		this.onChange = this.onChange.bind(this);
		this.updateLastAction = this.updateLastAction.bind(this);
		this.SearchStore = new SearchStore(this.updateLastAction, props);
	}

	// Added back in as it was causing issues with the search functionality
	componentDidUpdate(prevProps: ISearchProps) {
		const { term, pillProps, selectedResults } = this.props;
		if (JSON.stringify(prevProps.pillProps) !== JSON.stringify(pillProps)) {
			this.SearchStore.setupPillProps(pillProps);
		}
		if (term && term !== prevProps.term) {
			this.SearchStore.setTerm(term);
		}

		// During the user interaction items will be added or removed, a selected result will/should not be modified
		if (prevProps?.selectedResults?.length !== selectedResults?.length) {
			this.SearchStore.setSelectedResults(selectedResults || []);
		}
	}

	onChange(e: React.ChangeEvent<HTMLInputElement>): void {
		const { searchCallback, hideResultsOnBlankSearch = false } = this.props;
		const searchTerm = common.commonUtils.safe(() => e.target.value, '') || '';
		const canSearch = searchCallback && searchTerm.length >= this.minCharacterTrigger;
		const resetSearch = searchTerm === '' && !hideResultsOnBlankSearch;

		this.SearchStore.setTerm(searchTerm);
		if (resetSearch || canSearch) {
			this.SearchStore.setIsSearching(true);
			// debounce
			if (this.timeout) {
				clearTimeout(this.timeout);
				this.timeout = undefined;
			}
			this.timeout = setTimeout(() => {
				this.SearchStore.fetchResults().then(() => {
					/* Check for an edge case, where if the are no results found and a noResultText is configured
					    the results length was = 1 because of the noResultText message */
					this.resultCount = this.results[0]?.id === 'noResults' ? 0 : this.results.length;
				});
			}, this.onChangeMillisecondTimeout);
		} else {
			this.SearchStore.hideResults();
			this.SearchStore.resetPillProps();
		}
	}

	updateLastAction(action: TSearchAction) {
		this.lastAction = action;
	}

	get term() {
		return this.SearchStore.term;
	}

	get isSearching() {
		return this.SearchStore.isSearching;
	}

	get pillProps() {
		return this.SearchStore.pillProps;
	}

	get selectedResults() {
		return this.SearchStore.selectedResults;
	}

	removeSelectedResult(record: any) {
		return this.SearchStore.removeSelectedResult(record);
	}

	get results() {
		return this.SearchStore.results;
	}

	// Sort results so that the focused index follows a logical order on the UI
	get sortedResults() {
		const sortBy = (result: ISearchResult) => `${result.group || ''}${result.value}`;
		const sortedResults = sortByFunction(this.results, sortBy, 'ASC');
		return sortedResults;
	}

	get groupedResults() {
		const groupedResults: { [key: string]: ISearchResult[] } = {};
		for (const result of this.sortedResults) {
			const { group = '' } = result;
			if (!groupedResults[group]) {
				groupedResults[group] = [];
			}
			groupedResults[group].push(result);
		}
		return groupedResults;
	}

	get minCharacterTrigger(): number {
		return this.props.minCharacterTrigger || 3;
	}

	get onChangeMillisecondTimeout(): number {
		return this.props.onChangeMillisecondTimeout || 1000;
	}

	getAsset(isSearching: boolean) {
		return isSearching ? 'base.search.spinner' : 'base.search.magnificationGlass';
	}

	get resultGroupedElements() {
		// eslint-disable-next-line @typescript-eslint/naming-convention
		const { resultItemComponent: ResultItemComponent } = this.props;
		const groups = Object.keys(this.groupedResults);
		return groups.map((group, index) => {
			const resultGroupId = `result-group-${index}`;
			return (
				<div key={`${group}-${resultGroupId}`} role={'list'}>
					<ResultGroup id={resultGroupId} key={resultGroupId}>
						<Translate>{group}</Translate>
					</ResultGroup>
					<ResultGroupWrapper
						role={'list'}
						aria-labelledby={resultGroupId}
						key={`result-group-wrapper-${index}`}
					>
						{this.groupedResults[group].map((record, index2) => {
							record.focused = this.sortedResults.indexOf(record) === this.focusedIndex;
							const resultGroupElementId = `result-element-${index2}`;
							return (
								<ResultGroupElement
									key={resultGroupElementId}
									id={resultGroupElementId}
									item={record}
									isOpen={this.SearchStore.showResults}
									onClick={() => this.SearchStore.clickRecord(record)}
									onKeyDown={(e: any) => this.handleKeyDown(e, record)}
									role={'text'}
								>
									{ResultItemComponent ? (
										<ResultItemComponent record={record} />
									) : (
										<Translate>{record.value}</Translate>
									)}
								</ResultGroupElement>
							);
						})}
					</ResultGroupWrapper>
				</div>
			);
		});
	}

	get resultElements() {
		// eslint-disable-next-line @typescript-eslint/naming-convention
		const { resultItemComponent: ResultItemComponent } = this.props;
		const { showResults } = this.SearchStore;
		return this.results.map((record, index) => {
			record.focused = index === this.focusedIndex;
			const resultElementId = `result-element-${index}`;
			return (
				<ResultElement
					key={resultElementId}
					id={resultElementId}
					item={record}
					isOpen={showResults}
					onClick={() => this.SearchStore.clickRecord(record)}
					onKeyDown={(e: any) => this.handleKeyDown(e, record)}
				>
					{ResultItemComponent ? <ResultItemComponent record={record} /> : record.value}
				</ResultElement>
			);
		});
	}

	getPlaceholder(translationContext: ITranslationContextValue) {
		const { placeholder } = this.props;
		return placeholder ? translationContext.translate(placeholder) : placeholder;
	}

	getAutoFocus() {
		let autoFocus = false;
		if (this.lastAction !== undefined && SEARCH_ACTIONS.indexOf(this.lastAction) >= 0) {
			this.lastAction = undefined;
			autoFocus = true;
		}
		return autoFocus;
	}

	get searchLabel() {
		const { label = 'Search', hideLabel = true } = this.props;
		let labelComponent = (
			<Label id={this.searchLabelId} htmlFor={this.searchInputId}>
				{label}
			</Label>
		);
		if (hideLabel) {
			labelComponent = <VisuallyHidden>{labelComponent}</VisuallyHidden>;
		}
		return labelComponent;
	}

	get searchResults() {
		if (this.showSelectedResults) {
			const searchResultsComponent = (
				<SelectedResultsWrapper>
					{this.selectedResults.map((result) => {
						return (
							<SelectedResultPill
								key={`result-element-${result.value}`}
								onClick={() => this.removeSelectedResult(result)}
								onKeyDown={(e: any) => this.handlePillKeyDown(e, result)}
							>
								<SelectedResultGroup>
									<Translate>{result.group}</Translate>:
								</SelectedResultGroup>
								<SelectedResultValue>
									<Translate>{result.value}</Translate>
								</SelectedResultValue>
								<SelectedResultClear
									asset="base.search.selectedResultClear"
									onClick={() => this.SearchStore.reset()}
								/>
							</SelectedResultPill>
						);
					})}
				</SelectedResultsWrapper>
			);
			return searchResultsComponent;
		}
	}

	get showSelectedResults() {
		const { showSelectedResults = false } = this.props;
		return showSelectedResults;
	}

	onNavigation(key: string) {
		const { pageKeyTraverseSize = 3 } = this.props;
		switch (key) {
			case KEYS.UP_ARROW:
				if (this.SearchStore.showResults) {
					if (this.focusedIndex <= 0) {
						this.focusedIndex = this.results.length - 1;
					} else {
						this.focusedIndex = this.focusedIndex - 1;
					}
				} else {
					this.SearchStore.showResults = true;
				}
				break;
			case KEYS.DOWN_ARROW:
				if (this.SearchStore.showResults) {
					this.focusedIndex = (this.focusedIndex + 1) % this.results.length;
				} else {
					this.SearchStore.showResults = true;
				}
				break;
			case KEYS.PAGE_UP:
				if (this.SearchStore.showResults) {
					if (this.focusedIndex - pageKeyTraverseSize < 0 && this.focusedIndex !== 0) {
						this.focusedIndex = 0;
					} else if (this.focusedIndex - pageKeyTraverseSize < 0) {
						this.focusedIndex = this.results.length - 1;
					} else {
						this.focusedIndex = this.focusedIndex - pageKeyTraverseSize;
					}
				}
				break;
			case KEYS.PAGE_DOWN:
				if (this.SearchStore.showResults) {
					if (this.focusedIndex === this.results.length - 1) {
						this.focusedIndex = 0;
					} else if (this.focusedIndex + pageKeyTraverseSize > this.results.length - 1) {
						this.focusedIndex = this.results.length - 1;
					} else {
						this.focusedIndex = (this.focusedIndex + pageKeyTraverseSize) % this.results.length;
					}
				}
				break;
			case KEYS.HOME:
				if (this.SearchStore.showResults) {
					this.focusedIndex = 0;
				}
				break;
			case KEYS.END:
				if (this.SearchStore.showResults) {
					this.focusedIndex = this.results.length - 1;
				}
				break;
			case KEYS.ESCAPE:
				this.SearchStore.showResults = false;
				this.focusedIndex = -1;
				this.focusSearchInput = true;
				break;
			default:
				break;
		}
		this.setFocusedItem(this.focusedIndex);
	}

	handleKeyDown = (
		e: React.KeyboardEvent<HTMLLIElement> | React.KeyboardEvent<HTMLDivElement>,
		record?: ISearchResult,
	) => {
		const { key } = e;
		if (NAVIGATION_KEYS.indexOf(key as KEYS) !== -1) {
			e.preventDefault();
			e.stopPropagation();
			this.onNavigation(key);
		} else if (
			ACCEPT_KEYS.indexOf(key as KEYS) !== -1 &&
			!this.SearchStore.showResults &&
			!this.props.ignoreAcceptanceKeys
		) {
			e.preventDefault();
			this.SearchStore.showResults = true;
		} else if (key === KEYS.TAB) {
			this.focusedIndex = -1;
			this.SearchStore.showResults = false;
			this.focusSearchInput = true;
		} else if (
			ACCEPT_KEYS.indexOf(key as KEYS) !== -1 &&
			this.results.length > 0 &&
			this.focusedIndex >= 0 &&
			this.SearchStore.showResults
		) {
			e.stopPropagation();
			e.preventDefault();
			this.focusedIndex = -1;
			this.SearchStore.clickRecord(record);
			this.SearchStore.showResults = false;
			this.focusSearchInput = true;
		}
	};

	handlePillKeyDown = (e: any, record: any) => {
		const { key } = e;
		if (ACCEPT_KEYS.indexOf(key as KEYS) !== -1) {
			e.preventDefault();
			this.removeSelectedResult(record);
		}
	};

	setFocusedItem(index: number) {
		const { results } = this.SearchStore;
		for (let i = 0; i < results.length; i++) {
			results[i].focused = index === i;
		}
	}

	render() {
		const { className, iconPosition, groupSearchResults } = this.props;

		const inputProps = {
			role: 'combobox',
			id: this.searchInputId,
			type: 'search',
			value: this.term,
			focus: this.focusSearchInput,
			autoFocus: this.getAutoFocus(),
			onChange: this.onChange,
			onClick: () => this.SearchStore.displayResults,
			onKeyDown: (e: any) => this.handleKeyDown(e),
			'aria-labelledby': this.searchLabelId,
			'aria-haspopup': 'listbox',
			'aria-expanded': this.SearchStore.showResults,
			'aria-controls': this.SearchStore.showResults ? this.searchResultsId : null,
			'aria-autocomplete': 'list',
			'aria-describedby': `${this.searchInputId}_assistiveHint`,
			autoComplete: 'off',
		};

		const visuallyHiddenStyle = {
			position: 'absolute',
			left: '-10000px',
			top: 'auto',
			width: '1px',
			height: '1px',
			overflow: 'hidden'
		} as any;

		const iconProps = {
			asset: this.getAsset(this.isSearching),
			'aria-hidden': true,
			alt: '',
		};

		const pillProps = this.pillProps;

		// Used to force a render for the focus and selected results
		this.focusedIndex;
		this.selectedResults;

		return (
			<Wrapper className={className}>
				<TranslationContext.Consumer>
					{(translationContext: ITranslationContextValue) => {
						// push translated placeholder onto inputProps
						const placeholder = this.getPlaceholder(translationContext);
						Object.assign(inputProps, {
							placeholder,
							'aria-label': placeholder,
						});

						return (
							<React.Fragment>
								{this.searchLabel}
								<SearchBar id={this.searchBarId} aria-busy={this.SearchStore.isSearching}>
									{iconPosition === 'left' && <SearchIcon {...iconProps} />}
									{this.searchResults}
									{pillProps && pillProps.pillPosition === 'left' && <SearchPill {...pillProps} />}
									<div
										id="resultsStatus"
										role="status"
										aria-atomic="true"
										aria-live="polite"
										style={visuallyHiddenStyle}
									>
										{this.SearchStore.showResults
											? `${this.resultCount} search results found`
											: 'Search results empty'}
									</div>
									<Input {...inputProps} />
									{this.SearchStore.hasSelectedResults && (
										<SearchClear
											aria-label={'Clear'}
											iconProps={{ asset: 'base.search.cross' }}
											onClick={() => this.SearchStore.clearSelectedResults()}
											suppressAnalytics={true}
										/>
									)}
									{!common.commonUtils.isNullOrEmpty(this.term) &&
										!this.SearchStore.hasSelectedResults && (
											<SearchClear
												aria-label={'Clear'}
												iconProps={{
													asset: 'base.search.cross',
												}}
												onClick={() => this.SearchStore.reset()}
												suppressAnalytics={true}
											/>
										)}
									{pillProps && pillProps.pillPosition === 'right' && <SearchPill {...pillProps} />}
									{iconPosition === 'right' && <SearchIcon {...iconProps} />}
									<span id={`${this.searchInputId}_assistiveHint`} style={visuallyHiddenStyle}>
										<Translate>
											When autocomplete results are available use up and down arrows to review and
											enter to select.
										</Translate>
									</span>
								</SearchBar>
								<SearchResultElements
									id={this.searchResultsId}
									groupSearchResults={groupSearchResults}
									showResults={this.SearchStore.showResults}
									resultGroupedElements={this.resultGroupedElements}
									resultElements={this.resultElements}
									translationContext={translationContext}
								/>
							</React.Fragment>
						);
					}}
				</TranslationContext.Consumer>
				{this.SearchStore.showResults && (
					<ResultBackground
						onClick={() => {
							this.SearchStore.hideResults();
						}}
					/>
				)}
			</Wrapper>
		);
	}
}

export default InjectedStyledComponent(observer(Search), SEARCH_CATEGORY_KEY, SEARCH_COMPONENT_KEY);
